/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { bindActionCreators } from "redux";
import {
  ArrowLeft, FileText, Phone, Edit3, Save,
  CheckCircle2, XCircle,
} from "lucide-react";
import RenderSupportPage from "../RenderSupportPage";
import Loader from "../../Components/Loader";
import Member360Panel from "../components/Member360Panel";
import MemberProfileSkeleton from "../components/MemberProfileSkeleton";
import { supportActionCreators } from "../../services/Actions";
import {
  StatusBadge, PriorityBadge, QueueBadge, timeAgo,
} from "../components/TicketBadges";
import {
  TicketStatus, TicketPriority, TicketPrioritySelectable,
} from "../../contants";

// The working screen of the module: the reported issue on the left, live member
// context on the right. A ticket is a tagged record of the member's problem —
// not a conversation — so the left column shows the description, and the panel
// lets the agent look up the member's loans/funds/deductions to resolve it.

function TicketDetailed() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const {
    GetTicketById, GetMember360, GetMemberTickets,
    UpdateTicketStatus, EditTicket, UpdateTicketPriority,
    ClearTicketDetailed, ResetSupportResponse,
  } = bindActionCreators(supportActionCreators, dispatch);

  const support = useSelector((state) => state.support);
  const {
    ticket_detailed: ticket, member360, memberTickets,
    response, isLoading,
  } = support;

  const ticketId = new URLSearchParams(location.search).get("id");

  // Inline edit of the reported issue.
  const [isEditing, setIsEditing] = useState(false);
  const [editDescription, setEditDescription] = useState("");

  const loadTicket = () => {
    if (!ticketId) return;
    GetTicketById(ticketId);
  };

  useEffect(() => {
    loadTicket();
    return () => ClearTicketDetailed();
  }, [ticketId]);

  // The member panel depends on the ticket, so it loads on a second pass once
  // the staff_id is known.
  const staffId = ticket?.member?.staff_id || ticket?.staff_id;
  useEffect(() => {
    if (staffId) {
      GetMember360(staffId);
      GetMemberTickets(staffId);
    }
  }, [staffId]);

  useEffect(() => {
    if (response?.state === "SUCCESS") {
      toast.success(response?.message);
      setIsEditing(false);
      setTimeout(() => {
        ResetSupportResponse();
        loadTicket();
      }, 1000);
    } else if (response?.state === "ERROR") {
      toast.error(response?.message);
      setTimeout(() => ResetSupportResponse(), 1500);
    }
  }, [response]);

  const changeStatus = (status) => UpdateTicketStatus(ticketId, { status });

  const startEdit = () => {
    setEditDescription(ticket?.description || "");
    setIsEditing(true);
  };

  const saveEdit = () => {
    if (!editDescription.trim()) {
      toast.error("The issue description cannot be empty");
      return;
    }
    EditTicket(ticketId, { description: editDescription.trim() });
  };

  const selectCls =
    "px-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 focus:ring-indigo-500 focus:border-indigo-500";

  const status = String(ticket?.status || "").toUpperCase();
  const isClosed = status === "CLOSED";
  const isResolved = status === "RESOLVED";

  return (
    <RenderSupportPage title={ticket?.subject || "Ticket"}>
      {isLoading && <Loader />}
      <ToastContainer />

      <button
        onClick={() => history.push("/support/tickets")}
        className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 mb-4 -mt-4"
      >
        <ArrowLeft size={14} /> Back to queue
      </button>

      {/* Ticket header */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-4 mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-black text-slate-800 dark:text-slate-100">
              {ticket?.subject || "Untitled ticket"}
            </p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
              #{ticket?.ticket_no || "—"} · {ticket?.category || "Uncategorised"} ·
              opened {timeAgo(ticket?.created_at || ticket?.createdAt)} ·
              {" "}
              {ticket?.assigned_to?.name || ticket?.assigned_to_name || "Unassigned"}
            </p>
            {ticket?.phone && (
              <p className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 dark:text-slate-400 mt-1">
                <Phone size={11} /> {ticket.phone}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <QueueBadge queue={ticket?.queue} />
            <PriorityBadge priority={ticket?.priority} />
            <StatusBadge status={ticket?.status} />
          </div>
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-gray-700">
          <select
            value={ticket?.priority || TicketPriority.NORMAL}
            onChange={(e) => UpdateTicketPriority(ticketId, { priority: e.target.value })}
            className="px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-slate-600 dark:text-slate-300"
          >
            {/* Only Normal/Low are selectable. A legacy High/Urgent ticket keeps
                its value as an extra option so the dropdown isn't blank. */}
            {(TicketPrioritySelectable.includes(ticket?.priority)
              ? TicketPrioritySelectable
              : [ticket?.priority, ...TicketPrioritySelectable].filter(Boolean)
            ).map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>

          <div className="flex-1" />

          {!isClosed && !isResolved && (
            <button
              onClick={() => changeStatus(TicketStatus.PENDING)}
              className="px-3 py-1.5 text-xs font-bold rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100 transition-colors"
            >
              Awaiting Member
            </button>
          )}
          {!isClosed && !isResolved && (
            <button
              onClick={() => changeStatus(TicketStatus.RESOLVED)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm"
            >
              <CheckCircle2 size={13} /> Resolve
            </button>
          )}
          {/* Close is available directly — an agent can close an issue without
              resolving it first. */}
          {!isClosed && (
            <button
              onClick={() => changeStatus(TicketStatus.CLOSED)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-slate-600 text-white hover:bg-slate-700 transition-colors shadow-sm"
            >
              <XCircle size={13} /> Close Issue
            </button>
          )}
          {isClosed && (
            <button
              onClick={() => changeStatus(TicketStatus.OPEN)}
              className="px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
            >
              Reopen
            </button>
          )}
        </div>
      </div>

      {/* Split view: reported issue | member 360 */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Reported issue */}
        <div className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 flex flex-col">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-700 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <FileText size={15} className="text-indigo-600 dark:text-indigo-400" />
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Reported Issue</h3>
            </div>
            {!isEditing && (
              <button
                onClick={startEdit}
                className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                <Edit3 size={12} /> Edit
              </button>
            )}
          </div>

          <div className="flex-1 p-5">
            {isEditing ? (
              <div className="space-y-3">
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={6}
                  placeholder="Describe the member's issue…"
                  className={`${selectCls} w-full resize-none`}
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={saveEdit}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
                  >
                    <Save size={13} /> Save
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : ticket?.description ? (
              <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
                {ticket.description}
              </p>
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500 italic">
                No description was recorded for this ticket.
              </p>
            )}
          </div>

          {/* Meta footer — who attended to the customer and when. This is a
              record, not a thread, so there is no reply box. */}
          <div className="px-5 py-3 border-t border-slate-100 dark:border-gray-700 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              Attended by{" "}
              <span className="font-semibold text-slate-500 dark:text-slate-400">
                {ticket?.created_by?.name || ticket?.created_by_name || ticket?.assigned_to?.name || "—"}
              </span>
            </span>
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              {timeAgo(ticket?.created_at || ticket?.createdAt)}
            </span>
            {ticket?.member?.staff_id && (
              <button
                onClick={() =>
                  history.push(`/support/member-detailed?staff_id=${ticket.member.staff_id}`)
                }
                className="ml-auto text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Open full member profile →
              </button>
            )}
          </div>
        </div>

        {/* Member context */}
        <div className="lg:col-span-2">
          {/* While the ticket is still resolving there is no staff_id to look
              up yet, so the panel would briefly render "No member linked". */}
          {!member360?.profile && staffId ? (
            <MemberProfileSkeleton compact rows={4} />
          ) : (
            <Member360Panel
              member360={member360}
              memberTickets={memberTickets}
              compact
            />
          )}
        </div>
      </div>
    </RenderSupportPage>
  );
}

export default TicketDetailed;
